import { ComponentProps, useId, useRef } from 'react'
import { OverrideProperties } from 'type-fest'

import { SizeOptions } from '../../../types/types'
import { astraTwMerge } from '../../Utilities/astraTwMerge'
import { AstraInputWrapper, getHelperTextId, InputBase, inputTypographyStyles } from '../AstraInputWrapper/AstraInputWrapper'
import { useAstraPrivacyMode } from '../AstraPrivacyMode/AstraPrivacyMode'

export const textFieldTypes = ['date', 'datetime-local', 'email', 'month', 'number', 'password', 'search', 'tel', 'text', 'time', 'url', 'week'] as const

type TextFieldTypes = (typeof textFieldTypes)[number]

// Free-text input types whose content should be hidden in privacy mode. Date/time
// types and `password` are left untouched (native pickers / already hidden).
const privacyMaskableTypes = new Set<TextFieldTypes>(['email', 'number', 'search', 'tel', 'text', 'url'])

type Props = InputBase &
    OverrideProperties<
        ComponentProps<'input'>,
        {
            size?: SizeOptions
            type?: TextFieldTypes
        }
    >

/**
 * Primary UI component for user interaction
 *
 * This component is a text input field that allows users to input text.
 */
export const AstraTextField = ({
    ref,
    startIcon,
    endIcon,
    size = 'medium',
    className,
    label,
    placeholder,
    error,
    helperText,
    errorText,
    disabled,
    readOnly,
    fullWidth,
    onChange,
    type = 'text',
    ...props
}: Props) => {
    const inputRef = useRef<HTMLInputElement>(null)
    const id = useId()
    const isPrivacyMode = useAstraPrivacyMode()
    const resolvedType = isPrivacyMode && privacyMaskableTypes.has(type) ? 'password' : type

    return (
        <AstraInputWrapper
            id={id}
            label={label}
            labelHtmlFor={`${label ?? placeholder}-input`}
            helperText={helperText}
            errorText={errorText}
            error={error}
            disabled={disabled}
            readOnly={readOnly}
            fullWidth={fullWidth}
            className={className}
            size={size}
            startIcon={startIcon}
            endIcon={endIcon}
            onClick={() => inputRef.current?.focus()}
        >
            <input
                {...props}
                aria-describedby={helperText ? getHelperTextId(id) : undefined}
                onChange={onChange}
                readOnly={readOnly}
                disabled={disabled}
                placeholder={placeholder}
                name={label ?? placeholder}
                type={resolvedType}
                aria-label={label}
                className={astraTwMerge(
                    'h-full grow bg-inherit focus:outline-none active:outline-none data-[placeholder]:text-subtle',
                    inputTypographyStyles[size],
                    fullWidth ? 'w-full' : ''
                )}
                ref={ref}
                id={`${label ?? placeholder}-input`}
            />
        </AstraInputWrapper>
    )
}
